import { Component, ErrorInfo, ReactNode } from "react";

/**
 * Contains a react-notion-x render crash to the side peek.
 *
 * react-notion-x throws from deep in its render tree on malformed recordMap
 * shapes we don't anticipate (e.g. a non-string asset source →
 * "e2.split is not a function"). Without a boundary that bubbles to the
 * app-level TopErrorBoundary, which tears down and recreates the whole app.
 * This keeps the blast radius to the peek: it swaps in a compact message and
 * the rest of the canvas stays alive.
 */
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class NotionRenderBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  // Every crash here is a recordMap shape normalizeRecordMap doesn't yet repair,
  // and the fallback text alone gives no way to find out which one. Logging the
  // error and the react-notion-x component stack is what makes the next one
  // diagnosable from a user's console.
  componentDidCatch(error: Error, info: ErrorInfo) {
    // The error itself is worth keeping in production; the component stack is a
    // wall of minified react-notion-x internals that helps nobody there.
    console.error(
      "[NotionRenderBoundary] react-notion-x failed to render",
      error,
      import.meta.env?.DEV ? info.componentStack : "",
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-5 text-sm text-gray-500">
          Couldn&apos;t render part of this page.
        </div>
      );
    }
    return this.props.children;
  }
}
